import { type QueryClient, useQuery } from "@tanstack/react-query";
import { useApiClient } from "../useApiClient";

export function attachmentBytesQueryKey(attachmentId: string) {
  return ["attachments", "bytes", attachmentId] as const;
}

function isAttachmentBytesKey(key: readonly unknown[]): boolean {
  return key.length >= 2 && key[0] === "attachments" && key[1] === "bytes";
}

function revokeIfBlobUrl(value: unknown): void {
  if (typeof value === "string" && value.startsWith("blob:")) {
    URL.revokeObjectURL(value);
  }
}

// The object URL for an attachment's bytes lives in the react-query cache as the
// query's data, so its lifetime must be bound to the CACHE ENTRY, not to any one
// component. A thumbnail and the preview modal share the same query key, so a
// per-component unmount revoke would tear down the URL the other still renders.
//
// Register this ONCE against the app's QueryClient (see App.tsx). It revokes the
// URL when the entry is actually evicted (gcTime expiry, queryClient.clear(),
// manual removal) and, defensively, when a refetch supersedes the URL. A side
// map keyed by queryHash lets a superseding update revoke the URL it replaced.
// Returns an unsubscribe that also sweeps any still-tracked URLs, so tearing the
// client down (e.g. app unmount) leaks nothing.
export function manageAttachmentBlobUrlLifecycle(
  queryClient: QueryClient,
): () => void {
  const cache = queryClient.getQueryCache();
  const liveUrls = new Map<string, string>();

  const unsubscribe = cache.subscribe((event) => {
    if (!isAttachmentBytesKey(event.query.queryKey)) {
      return;
    }
    const hash = event.query.queryHash;

    if (event.type === "removed") {
      revokeIfBlobUrl(liveUrls.get(hash) ?? event.query.state.data);
      liveUrls.delete(hash);
      return;
    }

    // added / updated: if the entry now holds a fresh blob URL, revoke the one
    // it supersedes (a refetch) and remember the current one for eviction.
    const current = event.query.state.data;
    if (typeof current === "string" && current.startsWith("blob:")) {
      const previous = liveUrls.get(hash);
      if (previous !== undefined && previous !== current) {
        URL.revokeObjectURL(previous);
      }
      liveUrls.set(hash, current);
    }
  });

  return () => {
    unsubscribe();
    for (const url of liveUrls.values()) {
      revokeIfBlobUrl(url);
    }
    liveUrls.clear();
  };
}

export function useAttachmentBlobUrl(attachmentId: string, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: attachmentBytesQueryKey(attachmentId),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/api/attachments/{attachment_id}/bytes",
        {
          params: { path: { attachment_id: attachmentId } },
          parseAs: "blob",
        },
      );
      if (error !== undefined || !response.ok) {
        throw new Error(`Failed to load attachment bytes ${attachmentId}`);
      }
      const blob = data instanceof Blob ? data : new Blob([data as BlobPart]);
      return URL.createObjectURL(blob);
    },
    enabled: enabled && attachmentId.length > 0,
    // The URL is revoked by manageAttachmentBlobUrlLifecycle when this entry is
    // evicted; gcTime is the grace window after the last observer unmounts, long
    // enough that reopening the gallery reuses the URL instead of refetching.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60 * 1000,
  });
}
